import { useState, useEffect, useMemo } from "react";
import { useAuth } from "./useAuth";
import { useGetProjectTeamMembers } from "./projectTeam";

export const AVAILABLE_REGIONS = [
  "All Regions",
  "East Midlands",
  "Eastern Counties",
  "North West",
  "South West",
  "Southern",
  "West Midlands",
  "Yorkshire",
] as const;

export type Region = (typeof AVAILABLE_REGIONS)[number];

const parseRegion = (region: string | undefined | null): string | null => {
  if (region == null) return "";
  if (region === "") return "";
  if (typeof region !== "string") return "";
  const parts = region.split("~~");
  return parts[parts.length - 1] || "";
};

export const useUserRole = () => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { data: teamMembers, isLoading: isTeamLoading, isError } = useGetProjectTeamMembers();

  const [manualRegion, setManualRegion] = useState<string | null>(() =>
    localStorage.getItem("userRegion"),
  );

  useEffect(() => {
    const handleRegionChange = (event: CustomEvent<string | null>) => {
      setManualRegion(event.detail);
    };
    window.addEventListener("regionChanged" as any, handleRegionChange as any);
    return () =>
      window.removeEventListener(
        "regionChanged" as any,
        handleRegionChange as any,
      );
  }, []);

  const { role, region } = useMemo(() => {
    if (!isAuthenticated || !user?.email || !teamMembers) {
      return { role: null, region: null };
    }

    const normalizedEmail = user.email.toLowerCase().trim();
    const member = teamMembers.find(
      (m) => m.Email?.toLowerCase().trim() === normalizedEmail,
    );

    if (!member) return { role: null, region: null };

    const roleValue =
      member.RoleDisplayName?.trim() || member.Role?.trim() || null;

    const canManuallySelectRegion =
      roleValue === "Board Level" || roleValue === "System Administrator";

    if (!canManuallySelectRegion) {
      localStorage.removeItem("userRegion");
    }

    let resolvedRegion: string | null;
    if (canManuallySelectRegion && manualRegion !== null) {
      resolvedRegion = manualRegion;
    } else {
      resolvedRegion = parseRegion(member.Region);
    }

    return { role: roleValue, region: resolvedRegion };
  }, [isAuthenticated, user?.email, teamMembers, manualRegion]);

  const updateRegion = (newRegion: string | null) => {
    setManualRegion(newRegion);
    if (newRegion) {
      localStorage.setItem("userRegion", newRegion);
    } else {
      localStorage.removeItem("userRegion");
    }
    window.dispatchEvent(new CustomEvent("regionChanged", { detail: newRegion }));
  };

  return {
    role,
    region,
    updateRegion,
    isLoading: isAuthLoading || isTeamLoading,
    error: isError ? "Failed to load user data" : null,
  };
};
